/**
 * Payment-method display + filter options.
 *
 * Used by:
 *   - InvoiceTable + InvoiceFilters (admin Invoice Management)
 *   - Transactions list filter dropdown
 *
 * Backend stores raw values like `card`, `us_bank_account`, `cash`,
 * `check`, `zelle`, `venmo`, `credit`, `manual`, `stripe`. We normalise
 * those into a friendly label + colour for the badge.
 */

export const PAYMENT_METHOD_LABELS = {
  card:             { label: 'Card',         className: 'bg-sky-100 text-sky-800 border-sky-200' },
  // iter334bc — Stripe Link is a wallet that typically wraps a saved
  // card (or occasionally bank). Display as its own pill so admins
  // can see the rail, and reuse the card palette since surcharge is
  // charged the same way (see attempt_auto_charge).
  link:             { label: 'Link',         className: 'bg-sky-100 text-sky-800 border-sky-200' },
  us_bank_account:  { label: 'ACH',          className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  ach:              { label: 'ACH',          className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  bank:             { label: 'ACH',          className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  bank_transfer:    { label: 'ACH',          className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cash:             { label: 'Cash',         className: 'bg-amber-100 text-amber-800 border-amber-200' },
  check:            { label: 'Check',        className: 'bg-amber-100 text-amber-800 border-amber-200' },
  zelle:            { label: 'Zelle',        className: 'bg-violet-100 text-violet-800 border-violet-200' },
  venmo:            { label: 'Venmo',        className: 'bg-violet-100 text-violet-800 border-violet-200' },
  credit:           { label: 'Account Credit', className: 'bg-teal-100 text-teal-800 border-teal-200' },
  account_credit:   { label: 'Account Credit', className: 'bg-teal-100 text-teal-800 border-teal-200' },
  // iter334cb — External-platform passthroughs. Recorded when a
  // parent pays outside the academy stack; treated as offline for
  // reconciliation.
  zoho:             { label: 'Zoho',         className: 'bg-rose-100 text-rose-800 border-rose-200' },
  courtreserve:     { label: 'CourtReserve', className: 'bg-orange-100 text-orange-800 border-orange-200' },
  // iter307af — Treat raw "stripe" as a Card fallback. Stripe is a
  // processor, never a payment method. Backend now backfills the
  // actual rail (card / us_bank_account) on every row; this remains
  // as a last-line safety so the literal "Stripe" pill is never shown.
  stripe:           { label: 'Card',         className: 'bg-sky-100 text-sky-800 border-sky-200' },
  manual:           { label: 'Manual',       className: 'bg-slate-100 text-slate-700 border-slate-200' },
  auto_charge:      { label: 'Auto-Pay',     className: 'bg-sky-100 text-sky-800 border-sky-200' },
  other:            { label: 'Other',        className: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export const formatPaymentMethod = (raw) => {
  if (!raw) return null;
  const key = String(raw).toLowerCase();
  // `facility` and `online` are checkout intents (pay-at-desk vs pay-now),
  // not actual payment methods. Treat them as "no method yet" so the
  // table renders `—` and waits for the real method once payment lands.
  if (key === 'facility' || key === 'online') return null;
  // iter307ae — backend Transactions ledger emits synthetic
  // "auto_pay_card" / "auto_pay_us_bank_account" sentinels when an
  // invoice was auto-charged. Decode them here so the badge reads
  // "Auto-Pay · Card" / "Auto-Pay · ACH" instead of generic "Auto-Pay".
  if (key.startsWith('auto_pay_')) {
    const railKey = key.replace('auto_pay_', '');
    const railEntry = PAYMENT_METHOD_LABELS[railKey];
    if (railEntry) {
      return {
        label: `Auto-Pay · ${railEntry.label}`,
        className: railEntry.className,
        title: `Auto-charged via saved ${railEntry.label}`,
      };
    }
  }
  return PAYMENT_METHOD_LABELS[key] || {
    label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  };
};

/**
 * iter307b — Resolve the BEST display label for an invoice/payment row.
 *
 * Stripe-paid invoices land with `payment_method = "stripe"` and the
 * actual rail in `payment_method_type` (us_bank_account / card).
 * Auto-charged invoices land with `payment_method = "auto_charge"` and
 * the rail in `auto_pay_method`. Surface the rail when present so
 * admins see "ACH" instead of the generic "Stripe" or "Auto-Pay".
 *
 * Falls back to the top-level `payment_method` for offline / legacy
 * rows that don't carry a rail.
 */
export const resolveDisplayMethod = (row) => {
  if (!row) return null;
  const top = (row.payment_method || '').toLowerCase();
  const type = (row.payment_method_type || '').toLowerCase();
  const autoPay = (row.auto_pay_method || '').toLowerCase();

  // Stripe-paid: the rail is in payment_method_type
  if (top === 'stripe' && type) {
    const m = formatPaymentMethod(type);
    return m ? { ...m, title: `Stripe · ${m.label}` } : null;
  }
  // Auto-charged via saved PM: rail in auto_pay_method
  if (top === 'auto_charge' && autoPay) {
    const m = formatPaymentMethod(autoPay);
    if (!m) return null;
    return {
      label: `Auto-Pay · ${m.label}`,
      className: m.className,
      title: `Auto-charged via saved ${m.label}`,
    };
  }
  // Fallback for offline / legacy rows
  return formatPaymentMethod(top);
};

// Dropdown options used by both admin filter selects. `all` is the
// "no filter" sentinel — matches the backend's `payment_method=all` /
// `method=all` short-circuit. iter307af — removed "Stripe" as a
// filter option; admins filter by actual rail (Card / ACH) only.
// iter334bx.5 — Merged "ACH (Stripe Bank Transfer)" and "ACH (offline)"
// into a single "ACH" option. The academy only accepts ACH via Stripe;
// no offline ACH flow exists. The filter now maps `us_bank_account`
// as the primary value with legacy aliases (`ach`, `bank`,
// `bank_transfer`) folded server-side.
export const PAYMENT_METHOD_FILTER_OPTIONS = [
  { value: 'all',             label: 'All methods' },
  { value: 'card',            label: 'Card' },
  { value: 'us_bank_account', label: 'ACH', aliases: ['ach', 'bank', 'bank_transfer'] },
  { value: 'cash',            label: 'Cash' },
  { value: 'check',           label: 'Check' },
  { value: 'zelle',           label: 'Zelle' },
  { value: 'venmo',           label: 'Venmo' },
  { value: 'credit',          label: 'Account Credit' },
  { value: 'zoho',            label: 'Zoho' },
  { value: 'courtreserve',    label: 'CourtReserve' },
  { value: 'manual',          label: 'Manual (other)' },
];
